import { useEffect, useState } from "react";
import { Badge, Button, Card, CopyButton, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";

type State = { enabled: boolean; token: string | null; url: string | null };

export function PublicSearchSettingsTab() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ enabled: false, token: null, url: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getPublicSearchToken().then(setState).catch(() => {});
  }, []);

  async function generate() {
    if (state.enabled && !window.confirm(t("settings.publicSearch.confirmRegen"))) return;
    setBusy(true);
    try {
      setState(await api.generatePublicSearchToken());
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!window.confirm(t("settings.publicSearch.confirmRevoke"))) return;
    setBusy(true);
    try {
      setState(await api.revokePublicSearchToken());
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder>
      <Stack>
        <Group justify="space-between">
          <Title order={5}>{t("settings.publicSearch.title")}</Title>
          <Badge color={state.enabled ? "green" : "gray"}>
            {state.enabled ? t("settings.publicSearch.enabled") : t("settings.publicSearch.disabled")}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">{t("settings.publicSearch.desc")}</Text>

        {state.enabled && state.url && (
          <Group>
            <TextInput readOnly value={state.url} style={{ flex: 1 }} />
            <CopyButton value={state.url}>
              {({ copied, copy }) => (
                <Button variant="light" onClick={copy}>
                  {copied ? t("settings.publicSearch.copied") : t("settings.publicSearch.copy")}
                </Button>
              )}
            </CopyButton>
            <Button component="a" href={state.url} target="_blank" variant="default">
              {t("settings.publicSearch.open")}
            </Button>
          </Group>
        )}

        <Group>
          <Button onClick={() => void generate()} loading={busy}>
            {state.enabled ? t("settings.publicSearch.regenerate") : t("settings.publicSearch.generate")}
          </Button>
          {state.enabled && (
            <Button color="red" variant="light" onClick={() => void revoke()} loading={busy}>
              {t("settings.publicSearch.revoke")}
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
